import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/pages/homepage.css';

// Import category images
import animeImage from '../images/categories/anime.png';
import renaissancePetImage from '../images/categories/renaissance-pet.png';
import comicImage from '../images/categories/comic.png';
import spanielsOriginal from '../images/categories/spaniels-original.png';
import wantedPosterImage from '../images/categories/wanted-poster.png';
import framedMockup from '../images/categories/framed-mockup.png';
import posterMockup from '../images/categories/poster-mockup.png';

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isLoggedIn = !!user;

  const handleGetStarted = () => {
    if (isLoggedIn) {
      navigate('/create-v2');
    } else {
      navigate('/register');
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="homepage-hero">
        <h1>Transform Any Photo Into <span>Stunning Printable Art</span></h1>
        <p className="hero-subtitle">AI-powered styles. Museum-quality prints. Delivered to your door.</p>
        <button className="hero-cta-btn" onClick={handleGetStarted}>
          Create Your First Print Free →
        </button>
        <p className="hero-sub-text">No credit card required • 50 free credits to start</p>

        {/* Transformation Visual */}
        <div className="transform-visual">
          <div className="transform-box">
            <img src={spanielsOriginal} alt="Your Photo" />
            <div className="label">Your Photo</div>
          </div>
          <span className="transform-arrow">→</span>
          <div className="transform-box highlight">
            <div className="ai-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2Z"/>
              </svg>
              <span>AI</span>
            </div>
          </div>
          <span className="transform-arrow">→</span>
          <div className="transform-box">
            <img src={wantedPosterImage} alt="Printable Art" />
            <div className="label">Printable Art</div>
          </div>
        </div>
      </section>

      {/* Styles Section */}
      <section className="homepage-styles">
        <h2 className="section-title">Choose Your Style</h2>
        <p className="section-subtitle">12 unique AI art styles to transform your photos</p>

        <div className="styles-grid">
          <div className="style-card" onClick={() => navigate('/create-v2')}>
            <img src={animeImage} alt="Anime Cards" />
            <h3>Anime Cards</h3>
            <p>Anime-style trading cards</p>
          </div>
          <div className="style-card" onClick={() => navigate('/create-v2')}>
            <img src={renaissancePetImage} alt="Renaissance Pet" />
            <h3>Renaissance Pet</h3>
            <p>Oil painting pet portraits</p>
          </div>
          <div className="style-card" onClick={() => navigate('/create-v2')}>
            <img src={comicImage} alt="Comic Covers" />
            <h3>Comic Covers</h3>
            <p>Vintage comic book art</p>
          </div>
          <div className="style-card more-card" onClick={() => navigate('/create-v2')}>
            <div className="more-content">
              <span className="more-count">+9</span>
              <span className="more-text">more styles</span>
            </div>
            <h3>View All Styles</h3>
            <p>Watercolor, 3D, Caricature & more</p>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="homepage-how">
        <h2 className="section-title">How It Works</h2>
        <p className="section-subtitle">From photo to print in 3 simple steps</p>

        <div className="steps-container">
          <div className="step">
            <div className="step-num">1</div>
            <h3>Upload Photo</h3>
            <p>Drop in any photo of yourself, friends, pets, or family</p>
          </div>
          <div className="step">
            <div className="step-num">2</div>
            <h3>Pick Style & Format</h3>
            <p>Choose from 12 AI styles and select card or poster size</p>
          </div>
          <div className="step">
            <div className="step-num">3</div>
            <h3>Download or Print</h3>
            <p>Get instant digital files or order pro prints delivered</p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="homepage-products">
        <h2 className="section-title">Turn Your Creations Into Real Prints</h2>
        <p className="section-subtitle">Museum-quality prints delivered to your door, starting from just £9.99</p>

        <div className="products-grid">
          <div className="product-card">
            <img src={animeImage} alt="Trading Card" />
            <h3>Trading Cards</h3>
            <p>Glossy collectible cards. Perfect for gifts or your own collection.</p>
            <span className="product-price coming-soon">Coming Soon</span>
          </div>
          <div className="product-card">
            <img src={framedMockup} alt="Framed Print" />
            <h3>Framed Prints</h3>
            <p>Ready to hang wall art. Black or white frame options.</p>
            <span className="product-price">From £24.99</span>
          </div>
          <div className="product-card">
            <img src={posterMockup} alt="Poster Print" />
            <h3>Poster Prints</h3>
            <p>A4 and A3 sizes. Premium paper, unframed.</p>
            <span className="product-price">From £9.99</span>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="homepage-faq">
        <h2 className="section-title">Frequently Asked Questions</h2>
        <p className="section-subtitle">Everything you need to know</p>

        <div className="faq-grid">
          <div className="faq-item">
            <h3>How long does it take to create a print?</h3>
            <p>Most AI transformations complete in 1-2 minutes. You'll get an instant digital download, and printed orders ship within 2-3 business days.</p>
          </div>
          <div className="faq-item">
            <h3>What kind of photos work best?</h3>
            <p>Clear, well-lit photos with faces visible work best. Front-facing shots are ideal. The AI works with people, pets, and groups.</p>
          </div>
          <div className="faq-item">
            <h3>How much does it cost?</h3>
            <p>Each creation uses 50 credits. You get 50 free credits to start. Credit packs start from £5 for 250 credits (5 creations).</p>
          </div>
          <div className="faq-item">
            <h3>Can I print at home or do I have to order prints?</h3>
            <p>Both! Every creation gives you a high-resolution digital file you can download instantly. Or order professional prints delivered to your door.</p>
          </div>
          <div className="faq-item">
            <h3>What's your refund policy?</h3>
            <p>If you're not happy with a creation, contact us and we'll give you credits back. For printed orders, we offer reprints or refunds for quality issues.</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="homepage-final-cta">
        <h2>Ready to Create?</h2>
        <p>Start with 50 free credits. No credit card required.</p>
        <button className="hero-cta-btn" onClick={handleGetStarted}>
          Get Started Free →
        </button>
      </section>
    </>
  );
};

export default HomePage;
